import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import SectionHeading from '../components/ui/SectionHeading';
import ArticleCard from '../components/ui/ArticleCard';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import Button from '../components/ui/Button';
import { publishedArticles as blogPosts } from '../data/articles';

const Blog: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLeadMagnetForm, setShowLeadMagnetForm] = useState(false);
  const [email, setEmail] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % blogPosts.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + blogPosts.length) % blogPosts.length);
  };

  const handleLeadMagnetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const link = document.createElement('a');
    link.href = '/property-management-checklist.pdf';
    link.download = 'RentSparks-Property-Management-Checklist.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setShowLeadMagnetForm(false);
    setShowThankYou(true);
    setEmail('');
    setTimeout(() => setShowThankYou(false), 3000);

    try {
      const formDataObj = new FormData();
      formDataObj.append('form-name', 'checklist-download');
      formDataObj.append('email', email);
      formDataObj.append('name', email.split('@')[0]);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as any).toString(),
      });

      if (!response.ok) {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      // The checklist still downloads even if lead capture fails — log only, don't block delivery.
      console.error('checklist-download form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="blog" className="py-20 bg-[#FAFAF5] relative">
      <Toaster position="top-center" />
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#f0fdbc]/30 to-[#FAFAF5]"></div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <SectionHeading
          title="Blog"
          subtitle="Stay informed with our latest articles on property management trends, tips, and best practices. Email us at info@rentsparks.com if you'd like our vetted LA metro area vendor list or our property management checklist as a downloadable PDF."
        />

        <div className="relative max-w-6xl mx-auto">
          <div className="flex justify-center items-center">
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-md hover:shadow-lg transition-shadow duration-200"
              aria-label="Previous article"
            >
              <ChevronLeft className="w-6 h-6 text-secondary" />
            </button>

            <div className="w-full max-w-2xl px-4">
              <Link to={`/blog/${blogPosts[currentIndex].slug}`}>
                <ArticleCard {...blogPosts[currentIndex]} />
              </Link>
            </div>

            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-md hover:shadow-lg transition-shadow duration-200"
              aria-label="Next article"
            >
              <ChevronRight className="w-6 h-6 text-secondary" />
            </button>
          </div>

          <div className="flex justify-center mt-8 gap-3">
            {blogPosts.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  currentIndex === index
                    ? 'bg-secondary w-6'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          <div className="text-center mt-12 space-y-8">
            <Button
              variant="secondary"
              className="text-lg px-8 py-3"
              onClick={() => window.location.href = '/blog'}
            >
              View All Articles
            </Button>

            <div className="flex justify-center">
              <div className="bg-white p-8 rounded-lg shadow-custom hover:shadow-custom-hover transition-all duration-300 max-w-md w-full">
                <div className="flex items-center justify-center mb-6">
                  <Download className="h-8 w-8 text-secondary mr-3" />
                  <h3 className="font-abadi text-xl font-medium">Property Management Checklist</h3>
                </div>
                <p className="text-dark-gray mb-6 text-lg leading-relaxed">
                  Download our comprehensive checklist for managing rental properties effectively. Includes maintenance schedules, tenant screening criteria, and more.
                </p>
                <Button
                  variant="custom"
                  className="w-full bg-[#f3fcae] hover:bg-[#f3fcae]/90 text-dark text-lg py-3"
                  onClick={() => setShowLeadMagnetForm(true)}
                >
                  Get Checklist
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lead Magnet Form Modal */}
      {showLeadMagnetForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full m-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-abadi text-xl font-medium">Get Your Property Management Checklist</h3>
              <button
                onClick={() => setShowLeadMagnetForm(false)}
                className="text-dark-gray hover:text-dark text-2xl"
              >
                ×
              </button>
            </div>
            <form
              onSubmit={handleLeadMagnetSubmit}
              name="checklist-download"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              className="space-y-4"
            >
              <input type="hidden" name="form-name" value="checklist-download" />
              <p className="hidden">
                <label>Don't fill this out: <input name="bot-field" /></label>
              </p>
              <input type="hidden" name="name" value={email.split('@')[0]} />

              <div>
                <label className="block text-dark-gray mb-2 text-lg">
                  Email Address <span className="text-error">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-medium-gray focus:outline-none focus:border-secondary text-lg"
                  placeholder="your@email.com"
                />
              </div>
              <p className="text-sm text-dark-gray">
                Your download will start right away, and we'll send occasional tips to your inbox too.
              </p>
              <Button
                variant="custom"
                type="submit"
                className="w-full bg-[#3087cf] hover:bg-[#3087cf]/90 text-white text-lg py-3"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Me the Checklist'}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Thank You Modal */}
      {showThankYou && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full m-4 text-center">
            <div className="mb-4">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="font-abadi text-xl font-medium mb-2">Thank You!</h3>
              <p className="text-dark-gray text-lg">Your Property Management Checklist download should begin automatically.</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Blog;
